/**
 * Scroll-linked motion components for home page sections:
 *   <scroll-words>  lights up a statement word by word as it scrolls past
 *   <scroll-rail>   pins a product rail and pans it sideways on vertical scroll
 *   <scroll-story>  sticky media that wipes to the image of the active chapter
 *   <count-up>      counts a number up from zero when it enters the viewport
 *
 * Every component leaves the static, fully readable layout in place when
 * motion is disabled or the visitor prefers reduced motion.
 */

const motionOK = () => window.Theme?.motion() ?? true;
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

/** Base class: calls `update()` once per animation frame while the element is near the viewport. */
class ScrollElement extends HTMLElement {
  connectedCallback() {
    if (!motionOK()) return;
    this.classList.add('is-animated');
    this.setup?.();

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.requestUpdate();
      },
      { rootMargin: '25% 0px' }
    );
    this.observer.observe(this);
    window.addEventListener('scroll', this.requestUpdate, { passive: true });
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.requestUpdate);
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.frame);
  }

  requestUpdate = () => {
    if (!this.inView || this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  };

  onResize = () => {
    this.measure?.();
    this.requestUpdate();
  };

  /** 0 when the element's top reaches `start` (fraction of viewport), 1 when its bottom reaches `end`. */
  progress(start = 1, end = 0) {
    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight;
    const from = viewport * start;
    const to = viewport * end - rect.height;
    return clamp((from - rect.top) / (from - to));
  }
}

class ScrollWords extends ScrollElement {
  setup() {
    this.count = this.querySelectorAll('[data-word]').length;
  }

  update() {
    const lit = this.progress(0.85, 0.45) * (this.count + 2);
    this.style.setProperty('--lit', lit.toFixed(2));
  }
}

class ScrollRail extends ScrollElement {
  setup() {
    this.track = this.querySelector('[data-rail-track]');
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.desktop.addEventListener('change', this.onResize);
    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.track);
    this.measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.desktop?.removeEventListener('change', this.onResize);
    this.resizeObserver?.disconnect();
  }

  measure() {
    const pinned = this.desktop.matches && this.dataset.pin === 'true';
    this.classList.toggle('is-pinned', pinned);
    if (!pinned) {
      this.style.removeProperty('height');
      this.track.style.removeProperty('transform');
      return;
    }
    const viewport = this.querySelector('[data-rail-viewport]');
    this.distance = Math.max(0, this.track.scrollWidth - viewport.clientWidth);
    this.style.height = `${window.innerHeight + this.distance}px`;
  }

  update() {
    if (!this.classList.contains('is-pinned')) return;
    const rect = this.getBoundingClientRect();
    const travel = rect.height - window.innerHeight;
    const progress = travel > 0 ? clamp(-rect.top / travel) : 0;
    this.track.style.transform = `translate3d(${(-progress * this.distance).toFixed(1)}px, 0, 0)`;
    this.style.setProperty('--rail-progress', progress.toFixed(4));
  }
}

class ScrollStory extends ScrollElement {
  setup() {
    this.frames = [...this.querySelectorAll('[data-story-frame]')];
    this.chapters = [...this.querySelectorAll('[data-story-chapter]')];
    this.counter = this.querySelector('[data-story-current]');
    this.active = -1;
    this.setActive(0);

    this.chapterObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.setActive(this.chapters.indexOf(entry.target));
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    this.chapters.forEach((chapter) => this.chapterObserver.observe(chapter));
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.chapters.findIndex((chapter) => chapter.contains(event.target));
      if (index > -1) this.setActive(index);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.chapterObserver?.disconnect();
  }

  setActive(index) {
    if (index === this.active || index < 0) return;
    this.active = index;
    this.frames.forEach((frame, i) => {
      frame.classList.toggle('is-shown', i <= index);
      frame.classList.toggle('is-current', i === index);
    });
    this.chapters.forEach((chapter, i) => chapter.classList.toggle('is-current', i === index));
    if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
  }

  update() {
    this.style.setProperty('--story-progress', this.progress(0.5, 0.5).toFixed(4));
  }
}

class CountUp extends HTMLElement {
  connectedCallback() {
    const target = Number(this.dataset.value);
    if (!motionOK() || !Number.isFinite(target)) return;

    const decimals = (this.dataset.value.split('.')[1] || '').length;
    const format = (value) =>
      value.toLocaleString(document.documentElement.lang || undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    this.textContent = format(0);

    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.observer.disconnect();
      const start = performance.now();
      const duration = 1800;
      const step = (now) => {
        const t = clamp((now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 4);
        this.textContent = format(target * eased);
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

const components = {
  'scroll-words': ScrollWords,
  'scroll-rail': ScrollRail,
  'scroll-story': ScrollStory,
  'count-up': CountUp,
};
for (const [name, constructor] of Object.entries(components)) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
